import { useApiQuery } from '../useApiQuery';
import { leadKeys } from '@/lib/queryKeys';
import { formsEngineEndpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api';
import {
  formatSubmissionAnswers,
  type FormattedAnswer,
} from '@/lib/forms/intake-submission-answers';
import { isLeadFieldChoiceTarget } from '@/lib/forms/lead-field-targets';
import type {
  FormInstanceItem,
  FormInstanceSection,
  IntakeSubmissionDetail,
  UploadedFileAnswer,
} from '@/types/formsEngine';

/**
 * A submission's answer for the lead's Form tab. Same as the formatter's
 * `FormattedAnswer`, except `leadField` also carries the pseudo-target
 * `'serviceAddress'` — the one hint that isn't a `config.leadField` mapping
 * (see `serviceAddressItemKey` below).
 */
export type LeadFormAnswer = Omit<FormattedAnswer, 'leadField' | 'files'> & {
  leadField?: FormattedAnswer['leadField'] | 'serviceAddress';
};

export interface LeadFormSubmissionView {
  id: string;
  formName: string;
  submittedAt: string;
  /** Joined a lead that was already open: it filled none of the lead's fields. */
  appended: boolean;
  /** The channel the lead was attributed to, on the submission that created it; else null. */
  leadSource: string | null;
  answers: LeadFormAnswer[];
  files: UploadedFileAnswer[];
  consentText: string | null;
  consentAcceptedAt: string | null;
}

/**
 * True under the same rule operations' `hasTypedValue('ADDRESS', value)`
 * uses (`support/item-values.ts`): an object (not array) with a non-blank
 * `street` or `city`.
 */
function isAnsweredAddress(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return ['street', 'city'].some(
    (field) => typeof record[field] === 'string' && (record[field] as string).trim() !== '',
  );
}

/**
 * Mirrors operations' `intakeServiceAddress`
 * (`forms-engine/support/intake-respondent.ts`) exactly: the lead's service
 * address is filled only when the submission has exactly one answered
 * ADDRESS item, and that item's street/city/state/postalCode are all
 * present (postalCode at most 20 characters — the lead DTO's cap). When that
 * holds, this returns the item's key so the Form tab can show it a
 * "→ Service address" hint the same way a `config.leadField` mapping would.
 */
function serviceAddressItemKey(sections: FormInstanceSection[]): string | undefined {
  const answered = sections
    .flatMap((section) => section.items ?? [])
    .filter((item) => item.type === 'ADDRESS' && isAnsweredAddress(item.value));
  if (answered.length !== 1) return undefined;

  const value = answered[0]!.value as Record<string, unknown>;
  const part = (key: string) => (typeof value[key] === 'string' ? (value[key] as string).trim() : '');
  const street1 = part('street');
  const city = part('city');
  const state = part('state');
  const postalCode = part('postalCode');
  if (!street1 || !city || !state || !postalCode || postalCode.length > 20) return undefined;

  return answered[0]!.itemId;
}

/** `Lead.estimatedValue` is `Decimal(12, 2)`; operations drops a value outside [0, 10^10). */
const ESTIMATED_VALUE_LIMIT = 1e10;

/**
 * Whether conversion really filled the item's `config.leadField` from this
 * answer. Mirrors the two cases in operations' `mapAnswersToLeadFields` where
 * a mapped, answered question still sets nothing: a choice whose option has
 * no entry in `leadFieldOptionMap`, and an estimated value the lead can't
 * store.
 */
function fillsLeadField(item: FormInstanceItem): boolean {
  const target = item.config?.leadField;
  if (!target) return false;
  if (isLeadFieldChoiceTarget(target)) {
    const mapped =
      typeof item.value === 'string' ? item.config?.leadFieldOptionMap?.[item.value] : undefined;
    return typeof mapped === 'string' && mapped.trim() !== '';
  }
  if (target === 'estimatedValue') {
    const cents = Math.round(Number(item.value) * 100) / 100;
    return Number.isFinite(cents) && cents >= 0 && cents < ESTIMATED_VALUE_LIMIT;
  }
  return true;
}

/** One raw `IntakeSubmissionDetail` as the Form tab's flat, display-ready shape. */
export function toLeadFormSubmissionView(detail: IntakeSubmissionDetail): LeadFormSubmissionView {
  const addressItemKey = serviceAddressItemKey(detail.sections);
  const itemsById = new Map(
    detail.sections.flatMap((section) => section.items ?? []).map((item) => [item.itemId, item]),
  );
  const flatAnswers = formatSubmissionAnswers(detail.sections).flatMap((section) => section.answers);

  // A hint says "this answer filled that lead field", so it only appears
  // where that is true. An appended submission filled nothing at all.
  function hintFor(answer: FormattedAnswer): LeadFormAnswer['leadField'] {
    if (detail.appended) return undefined;
    if (!answer.leadField) return answer.key === addressItemKey ? 'serviceAddress' : undefined;
    const item = itemsById.get(answer.key);
    return item && fillsLeadField(item) ? answer.leadField : undefined;
  }

  // FILE_UPLOAD answers become a submission-level download list instead of an
  // inline row — the same value the formatter already carries as `.files`.
  const answers: LeadFormAnswer[] = flatAnswers
    .filter((answer) => !answer.files)
    .map(({ files: _files, ...answer }) => ({ ...answer, leadField: hintFor(answer) }));
  const files = flatAnswers.flatMap((answer) => answer.files ?? []);

  return {
    id: detail.id,
    formName: detail.templateName,
    submittedAt: detail.submittedAt,
    appended: detail.appended,
    leadSource: detail.leadSource,
    answers,
    files,
    consentText: detail.consentText,
    consentAcceptedAt: detail.consentAcceptedAt,
  };
}

/**
 * A lead's intake form submissions, newest first, for its Form tab. Any lead
 * can have them — a submission from someone with an open lead is appended to
 * that lead whatever its source — so every lead page asks (one indexed
 * lookup; an empty list for most leads).
 */
export function useLeadFormSubmissions(leadId: string, options?: { enabled?: boolean }) {
  return useApiQuery<IntakeSubmissionDetail[], LeadFormSubmissionView[]>({
    queryKey: leadKeys.formSubmissions(leadId),
    queryFn: () =>
      apiGet<IntakeSubmissionDetail[]>(
        operationsClient,
        formsEngineEndpoints.intakeSubmissionsByLead(leadId),
      ),
    select: (data) =>
      [...data]
        .sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())
        .map(toLeadFormSubmissionView),
    enabled: Boolean(leadId) && (options?.enabled ?? true),
    showErrorToast: false,
  });
}
